import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import ReactDOM from "react-dom";

import {
  FiAlertCircle,
  FiCheck,
  FiCheckCircle,
  FiClock,
  FiPauseCircle,
  FiRefreshCw,
  FiSlash,
  FiX,
  FiXCircle,
} from "react-icons/fi";

import API from "../../../../../utils/apiCalling";
import { config } from "../../../../../utils/apiUrl";
import useSwal from "../../../../../hooks/useSwal";

import "./ChangeTaskStatus.css";

const TASK_STATUS_OPTIONS = [
  {
    value: "PENDING",
    label: "Pending",
    description:
      "Task is created but work has not started yet.",
    icon: FiClock,
    className: "pending",
  },
  {
    value: "IN_PROGRESS",
    label: "In Progress",
    description:
      "Employee has started working on this task.",
    icon: FiRefreshCw,
    className: "in-progress",
  },
  {
    value: "WAITING_FOLLOW_UP",
    label: "Waiting Follow-up",
    description:
      "Task is waiting for customer, employee, or another follow-up.",
    icon: FiClock,
    className: "follow-up",
  },
  {
    value: "ON_HOLD",
    label: "On Hold",
    description:
      "Task is temporarily paused and will continue later.",
    icon: FiPauseCircle,
    className: "on-hold",
  },
  {
    value: "COMPLETED",
    label: "Completed",
    description:
      "Task work has been completed successfully.",
    icon: FiCheckCircle,
    className: "completed",
  },
  {
    value: "CLOSED",
    label: "Closed",
    description:
      "Task has been reviewed and permanently closed.",
    icon: FiXCircle,
    className: "closed",
  },
  {
    value: "CANCELLED",
    label: "Cancelled",
    description:
      "Task is no longer required and has been cancelled.",
    icon: FiSlash,
    className: "cancelled",
  },
];

const formatLabel = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map(
      (word) =>
        word.charAt(0).toUpperCase() +
        word.slice(1)
    )
    .join(" ");

const ChangeTaskStatus = ({
  open,
  task,
  onClose,
  onSuccess,
}) => {
  const api = useMemo(
    () => new API(),
    []
  );

  const swal = useSwal();

  const [status, setStatus] =
    useState("PENDING");

  const [progress, setProgress] =
    useState(0);

  const [note, setNote] =
    useState("");

  const [error, setError] =
    useState("");

  const [submitting, setSubmitting] =
    useState(false);

  useEffect(() => {
    if (!open || !task) {
      return;
    }

    setStatus(
      task.status || "PENDING"
    );

    setProgress(
      Number(task.progress || 0)
    );

    setNote("");
    setError("");
    setSubmitting(false);
  }, [open, task]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const oldOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      "hidden";

    const handleEscape = (event) => {
      if (
        event.key === "Escape" &&
        !submitting
      ) {
        onClose?.();
      }
    };

    window.addEventListener(
      "keydown",
      handleEscape
    );

    return () => {
      document.body.style.overflow =
        oldOverflow;

      window.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, [
    open,
    submitting,
    onClose,
  ]);

  const handleStatusSelect = (
    nextStatus
  ) => {
    setStatus(nextStatus);
    setError("");

    if (
      nextStatus === "COMPLETED"
    ) {
      setProgress(100);
    }

    if (
      nextStatus === "PENDING"
    ) {
      setProgress(0);
    }
  };

  const handleSubmit = async (
    event
  ) => {
    event.preventDefault();

    if (!task?._id) {
      setError(
        "Task information is missing."
      );

      return;
    }

    if (
      status === task.status &&
      Number(progress) ===
        Number(task.progress || 0)
    ) {
      setError(
        "Please select a different status or progress value."
      );

      return;
    }

    try {
      setSubmitting(true);
      setError("");

      const response =
        await api.patch(
          config.changeTaskStatus(
            task._id
          ),
          {
            status,
            progress:
              Number(progress),
            note: note.trim(),
          }
        );

      if (
        response?.status !==
        "success"
      ) {
        throw new Error(
          response?.message ||
            "Unable to update task status."
        );
      }

      await swal.success({
        icon: "success",
        title:
          "Task Status Updated",
        text:
          response?.message ||
          "Task status updated successfully.",
        timer: 1400,
        showConfirmButton: false,
      });

      if (
        typeof onSuccess ===
        "function"
      ) {
        await onSuccess(
          response?.data ||
            null
        );
      }
    } catch (requestError) {
      console.error(
        "Change task status error:",
        requestError
      );

      const message =
        requestError?.response?.data
          ?.message ||
        requestError?.data?.message ||
        requestError?.message ||
        "Unable to update task status.";

      setError(message);

      swal.error({
        icon: "error",
        title:
          "Unable to change status",
        text: message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || !task) {
    return null;
  }

  return ReactDOM.createPortal(
    <div className="task-status-modal-root">
      <button
        type="button"
        className="task-status-backdrop"
        onClick={() => {
          if (!submitting) {
            onClose?.();
          }
        }}
        aria-label="Close task status popup"
      />

      <section
        className="task-status-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="task-status-title"
      >
        <header className="task-status-header">
          <div className="task-status-header-content">
            <span className="task-status-header-icon">
              <FiRefreshCw />
            </span>

            <div>
              <small>
                TASK MANAGEMENT
              </small>

              <h2 id="task-status-title">
                Change Task Status
              </h2>
            </div>
          </div>

          <button
            type="button"
            className="task-status-close"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
          >
            <FiX />
          </button>
        </header>

        <form
          className="task-status-form"
          onSubmit={handleSubmit}
        >
          <div className="task-status-body">
            {error ? (
              <div className="task-status-error">
                <FiAlertCircle />

                <span>{error}</span>
              </div>
            ) : null}

            <section className="task-status-task-card">
              <span className="task-status-task-icon">
                {String(
                  task?.assignedTo?.name ||
                    "T"
                )
                  .charAt(0)
                  .toUpperCase()}
              </span>

              <div>
                <small>
                  {task.taskCode ||
                    "TASK"}
                </small>

                <strong>
                  {task.title}
                </strong>

                <span>
                  Assigned to{" "}
                  {task?.assignedTo?.name ||
                    "employee"}
                </span>
              </div>
            </section>

            <div className="task-status-current-row">
              <span>
                Current Status
              </span>

              <strong>
                {formatLabel(
                  task.status
                )}
              </strong>
            </div>

            <section className="task-status-options">
              {TASK_STATUS_OPTIONS.map(
                (option) => {
                  const Icon =
                    option.icon;

                  const selected =
                    status ===
                    option.value;

                  const current =
                    task.status ===
                    option.value;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      className={`task-status-option ${option.className} ${
                        selected
                          ? "selected"
                          : ""
                      }`}
                      onClick={() =>
                        handleStatusSelect(
                          option.value
                        )
                      }
                      disabled={
                        submitting
                      }
                    >
                      <span className="task-status-option-icon">
                        <Icon />
                      </span>

                      <div>
                        <strong>
                          {option.label}

                          {current ? (
                            <small>
                              Current
                            </small>
                          ) : null}
                        </strong>

                        <p>
                          {
                            option.description
                          }
                        </p>
                      </div>

                      <span className="task-status-radio">
                        {selected ? (
                          <FiCheck />
                        ) : null}
                      </span>
                    </button>
                  );
                }
              )}
            </section>

            <section className="task-status-progress-section">
              <div className="task-status-progress-heading">
                <div>
                  <strong>
                    Task Progress
                  </strong>

                  <span>
                    Update completion percentage with the status.
                  </span>
                </div>

                <strong>
                  {progress}%
                </strong>
              </div>

              <input
                type="range"
                min="0"
                max="100"
                step="5"
                value={progress}
                onChange={(event) =>
                  setProgress(
                    Number(
                      event.target.value
                    )
                  )
                }
                disabled={
                  submitting ||
                  status ===
                    "COMPLETED"
                }
              />

              <div className="task-status-progress-bar">
                <span
                  style={{
                    width: `${progress}%`,
                  }}
                />
              </div>
            </section>

            <div className="task-status-note-field">
              <label htmlFor="task-status-note">
                Status Change Note
              </label>

              <textarea
                id="task-status-note"
                value={note}
                onChange={(event) =>
                  setNote(
                    event.target.value
                  )
                }
                placeholder="Add reason, work update or status change note..."
                rows={4}
                maxLength={1000}
                disabled={submitting}
              />

              <small>
                {note.length}/1000
              </small>
            </div>
          </div>

          <footer className="task-status-footer">
            <button
              type="button"
              className="task-status-cancel"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="task-status-submit"
              disabled={submitting}
            >
              {submitting
                ? "Updating..."
                : "Update Status"}
            </button>
          </footer>
        </form>
      </section>
    </div>,
    document.body
  );
};

export default ChangeTaskStatus;